import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
	getArInvoices,
	getRequestActivity,
	getAttribution,
	getReimbursements,
	inr,
} from '../data';
import type { ArInvoice, RequestActivity, Attribution, Reimbursements } from '../types';

/** Cross-cutting findings: AR gaps, workload, Zoho attribution, duplicate claims. */
export const Findings: React.FC = () => {
	const [ar, setAr] = useState<ArInvoice[] | null>(null);
	const [req, setReq] = useState<RequestActivity | null>(null);
	const [attr, setAttr] = useState<Attribution | null>(null);
	const [reimb, setReimb] = useState<Reimbursements | null>(null);

	useEffect(() => {
		let on = true;
		getArInvoices({ status: 'void', limit: 100 }).then(r => on && setAr(r.data));
		getRequestActivity().then(r => on && setReq(r));
		getAttribution().then(r => on && setAttr(r));
		getReimbursements().then(r => on && setReimb(r));
		return () => {
			on = false;
		};
	}, []);

	const voidWithBal = (ar || []).filter(i => i.flag === 'void_with_balance');

	return (
		<div className='space-y-6'>
			<Panel title='Void invoices still carrying a balance' subtitle='Books-integrity defect — a void invoice should be zero'>
				{!ar ? (
					<Spin />
				) : (
					<table className='w-full text-sm'>
						<thead className='text-gray-500 text-xs uppercase'>
							<tr>
								<th className='text-left py-2'>Invoice</th>
								<th className='text-left py-2'>Customer</th>
								<th className='text-right py-2'>Balance</th>
							</tr>
						</thead>
						<tbody>
							{voidWithBal.slice(0, 10).map(i => (
								<tr key={i.zoho_invoice_id} className='border-t border-gray-100'>
									<td className='py-2 text-gray-800'>{i.invoice_number}</td>
									<td className='py-2 text-gray-600'>{i.customer_name}</td>
									<td className='py-2 text-right font-medium text-red-600'>{inr(i.balance)}</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</Panel>

			<Panel title='Payment-request workload per mailbox' subtitle={req?.note || ''}>
				{!req ? (
					<Spin />
				) : (
					<table className='w-full text-sm'>
						<thead className='text-gray-500 text-xs uppercase'>
							<tr>
								<th className='text-left py-2'>Mailbox</th>
								<th className='text-right py-2'>Threads</th>
								<th className='text-right py-2'>Requests</th>
								<th className='text-right py-2'>Processed</th>
								<th className='text-right py-2'>No reply</th>
							</tr>
						</thead>
						<tbody>
							{req.byMailbox.slice(0, 10).map(m => (
								<tr key={m.account_email} className='border-t border-gray-100'>
									<td className='py-2 text-gray-800'>{m.account_email.split('@')[0]}</td>
									<td className='py-2 text-right text-gray-600'>{m.threads}</td>
									<td className='py-2 text-right'>{m.requests}</td>
									<td className='py-2 text-right text-green-600'>{m.processed}</td>
									<td className='py-2 text-right text-red-600'>{m.no_reply_computed}</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</Panel>

			<Panel
				title='Zoho record attribution'
				subtitle='Who enters and approves — accountability gaps flagged'>
				{!attr ? (
					<Spin />
				) : (
					<div className='space-y-3'>
						<div className='grid grid-cols-3 gap-3'>
							<Flag
								label='Bills via generic login'
								value={attr.flags.generic_account_bills}
								sub={inr(attr.flags.generic_account_value)}
							/>
							<Flag label='Bills without approver' value={attr.flags.bills_without_approver} />
							<Flag label='Vendor-payment attribution' value='none' isText />
						</div>
						<div className='text-xs text-gray-500 space-y-1'>
							{Object.entries(attr.availability).map(([k, v]) => (
								<div key={k}>
									<span className='font-medium text-gray-600'>{k}</span>: {v}
								</div>
							))}
						</div>
					</div>
				)}
			</Panel>

			<Panel
				title='Duplicate reimbursement claims'
				subtitle={reimb?.duplicateSummary.note || ''}>
				{!reimb ? (
					<Spin />
				) : (
					<>
						<div className='grid grid-cols-3 gap-3 mb-3'>
							<Flag label='Duplicate groups' value={reimb.duplicateSummary.groups} />
							<Flag label='Extra copies' value={reimb.duplicateSummary.extra_copies} />
							<Flag label='Extra value' value={inr(reimb.duplicateSummary.extra_value)} isText />
						</div>
						<table className='w-full text-sm'>
							<tbody>
								{reimb.duplicates.slice(0, 8).map((d, i) => (
									<tr key={i} className='border-t border-gray-100'>
										<td className='py-2 text-gray-800'>{d.claimant_raw}</td>
										<td className='py-2 text-gray-500 text-xs'>
											{String(d.claim_date).slice(0, 10)} · {d.description.slice(0, 40)}
										</td>
										<td className='py-2 text-right'>
											<span className='text-red-600'>×{d.copies}</span>{' '}
											<span className='font-medium'>{inr(d.amount)}</span>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</>
				)}
			</Panel>
		</div>
	);
};

const Panel: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> = ({
	title,
	subtitle,
	children,
}) => (
	<section className='bg-white rounded-lg border border-gray-200 p-5'>
		<h3 className='font-semibold text-gray-900'>{title}</h3>
		{subtitle && <p className='text-xs text-gray-400 mb-3'>{subtitle}</p>}
		{children}
	</section>
);

const Flag: React.FC<{ label: string; value: number | string; sub?: string; isText?: boolean }> = ({
	label,
	value,
	sub,
	isText,
}) => (
	<div className='p-3 rounded-lg bg-gray-50 border border-gray-100'>
		<div className='text-xs text-gray-500'>{label}</div>
		<div className={`font-bold text-gray-900 ${isText ? 'text-base' : 'text-xl'}`}>
			{typeof value === 'number' ? value.toLocaleString() : value}
		</div>
		{sub && <div className='text-xs text-gray-400'>{sub}</div>}
	</div>
);

const Spin: React.FC = () => (
	<div className='py-6 text-center text-gray-400'>
		<Loader2 className='animate-spin inline' size={18} />
	</div>
);
